import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { FaExternalLinkAlt, FaDatabase } from "react-icons/fa";
import { useSocketState } from "../../helpers/useSocket";
import BracketSet from '../../components/Bracket/BracketSet';
import groupBy from 'lodash/groupBy'
import { getBackendUrl } from '../../helpers/url';

// Base URL for backend requests from environment variable
const BACKEND_BASE_URL = getBackendUrl();

function BracketDashboard() {

	const { socket } = useOutletContext()

	const [tournament] = useSocketState('Tournament');

	function handleTournamentSlugChange(el) {
		setTournamentSlug(el.target.value);
	}
	const [tournamentSlug, setTournamentSlug] = useState(null);

	useEffect(() => {
		if (tournament?.bracket?.slug && !tournamentSlug) {
			const cleaned = String(tournament.bracket.slug).replace("tournament/", "");
			setTournamentSlug(cleaned);
		}
	}, [tournament, tournamentSlug]);

	// track expanded/collapsed state for each phase by id
	const [expandedPhases, setExpandedPhases] = useState({});

	// track seeding panel visibility per event id
	const [showSeeding, setShowSeeding] = useState({});

	function toggleSeeding(eventId) {
		setShowSeeding(prev => ({ ...prev, [eventId]: !prev[eventId] }));
	}

	function getEventSeedGroups(event) {
		// Returns array of { phase, phaseGroup, seeds[] } — one entry per phaseGroup that has seeds
		const groups = [];
		for (const phase of event.phases ?? []) {
			for (const pg of phase.phaseGroups?.nodes ?? []) {
				const seeds = (pg.seeds?.nodes ?? [])
					.filter(s => s.entrant)
					.sort((a, b) => a.groupSeedNum - b.groupSeedNum);
				if (seeds.length > 0) groups.push({ phase, phaseGroup: pg, seeds });
			}
		}
		return groups;
	}

	function togglePhase(id, defaultExpanded) {
		setExpandedPhases(prev => ({ ...prev, [id]: typeof defaultExpanded === 'undefined' ? !prev[id] : defaultExpanded }));
	}

	// track which phase is currently being refreshed (null = full bracket loading)
	const [loadingPhaseId, setLoadingPhaseId] = useState(null);

	useEffect(() => {
		if (!tournament?.bracketLoading) {
			setLoadingPhaseId(null);
		}
	}, [tournament?.bracketLoading]);


	//Ideally this should be able to handle multiple different format
	//And be able to return the same slug
	//https://www.start.gg/tournament/egc-masters-finals-2025/event/main-event/brackets/2124223/3097036
	//https://www.start.gg/tournament/egc-masters-finals-2025/
	//tournament/egc-masters-finals-2025/
	//egc-masters-finals-2025
	function handleGetTournament() {
		const slug = tournamentSlug //TODO insert regex here
		socket.emit('setTournamentSlug', slug);
	}

	function handleRefreshPhase(phaseId) {
		setLoadingPhaseId(phaseId);
		socket.emit('updateBracketPhase', phaseId);
	}

	return <div>
		<div className='flex flex-col mb-4'>
			<label>Tournament Slug/ID</label>
			<div className='flex flex-col'>
				<div className='flex flex-row items-center'>
					{/*TODO Should possibly use the tournament.tournamentKey instead */}
					<input type="text" id="mapDraftUrl" name="mapDraftUrl" className='w-56 mr-8' value={tournamentSlug ?? ''} onChange={handleTournamentSlugChange} />
					<button className='h-8 my-auto mr-2' onClick={handleGetTournament} disabled={tournament?.bracketLoading}>Get Tournament</button>
					{tournament?.bracketLoading && loadingPhaseId === null && <span className='ml-2 text-sm text-gray-400'>Loading...</span>}
				</div>
				<h1>Using {tournament?.bracketProvider}</h1>
			</div>
		</div>

		{tournament?.bracket &&
			tournament?.bracket?.events?.map(event =>
				<div>
					<div className='flex items-center gap-2 mb-1'>
						<h1 className='font-bold text-xl'>{event.name}</h1>
						<button
							className='px-2 py-0.5 text-sm rounded border border-gray-300 hover:bg-gray-100'
							onClick={() => toggleSeeding(event.id)}
						>
							{showSeeding[event.id] ? 'Hide Seeding' : 'Show Seeding'}
						</button>
					</div>
					{showSeeding[event.id] && (() => {
						const groups = getEventSeedGroups(event);
						return groups.length > 0 ? (
							<div className='mb-3 ml-2 flex flex-row gap-2'>
								{groups.map(({ phase, phaseGroup, seeds }, i) => (
									<div key={phaseGroup.id} className='border border-gray-200 rounded p-2'>
										{groups.length > 1 && <div className='text-xs font-semibold text-gray-500 mb-1'>{phase.name} — Pool #{i + 1}</div>}
										<div className='overflow-y-auto min-w-64 max-h-64'>
											<table className='text-sm w-full'>
												<thead>
													<tr className='text-left text-gray-500 border-b border-gray-200'>
														<th className='pr-4 pb-1'>Seed</th>
														<th className='pb-1'>Entrant</th>
													</tr>
												</thead>
												<tbody>
													{seeds.map(seed => (
														<tr key={seed.entrant.id} className='border-b border-gray-100 last:border-0'>
															<td className='pr-4 py-0.5 text-gray-500 whitespace-nowrap'>{seed.groupSeedNum} <span className='text-gray-400'>({seed.seedNum})</span></td>
															<td className='py-0.5'>{seed.entrant.name}</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</div>
								))}
							</div>
						) : <p className='text-sm text-gray-400 mb-3 ml-2'>No seeding data — refresh a phase first.</p>;
					})()}
					{event.phases.map(phase =>
						<div className='ml-4 mb-1' key={phase.id}>
							<div className='flex items-center'>
								<button
									type="button"
									onClick={() => togglePhase(phase.id)}
									className='mr-2 w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200'
									aria-expanded={!!(expandedPhases[phase.id] ?? (phase.state === 'ACTIVE'))}
									aria-controls={`phase-${phase.id}`}
								>
									{(expandedPhases[phase.id] ?? (phase.state === 'ACTIVE')) ? '▾' : '▸'}
								</button>

								<h1 className='text-lg'>{phase.name}</h1>
								<span className='mx-2 text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-gray-200 text-gray-600'>{phase.bracketType}</span>
								<span className='mr-2 text-xs text-gray-400'>{phase.state} · {phase.id}</span>
								<div className='flex items-center gap-1'>
									<Link to={phase.bracketType === 'ROUND_ROBIN' ? `/RoundRobinDisplay?id=${phase.id}` : `/BracketDisplay?id=${phase.id}`} target='_blank'>
										<button className='bg-teal-700 hover:bg-teal-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm'>Bracket View<FaExternalLinkAlt className='text-xs' /></button>
									</Link>
									<Link to={`${BACKEND_BASE_URL}/api/vmix/startggBracket/${phase.id}`} target='_blank'>
										<button className='bg-indigo-700 hover:bg-indigo-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm' title='startggBracket'><FaDatabase className='text-xs' />Bracket</button>
									</Link>
									{phase.bracketType === 'ROUND_ROBIN' && <>
										<Link to={`${BACKEND_BASE_URL}/api/vmix/startggRoundRobinStandings/${phase.id}`} target='_blank'>
											<button className='bg-indigo-700 hover:bg-indigo-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm' title='startggRoundRobinStandings'><FaDatabase className='text-xs' />Standings</button>
										</Link>
										<Link to={`${BACKEND_BASE_URL}/api/vmix/startggRoundRobinGrid/${phase.id}`} target='_blank'>
											<button className='bg-indigo-700 hover:bg-indigo-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm' title='startggRoundRobinGrid'><FaDatabase className='text-xs' />Grid</button>
										</Link>
									</>}
								</div>
								<button className='ml-2 px-2 py-0.5 text-sm rounded border border-gray-300 hover:bg-gray-100' onClick={() => handleRefreshPhase(phase.id)} disabled={tournament?.bracketLoading}>Refresh</button>
								{loadingPhaseId === phase.id && <span className='ml-1 text-sm text-gray-400'>Loading...</span>}
							</div>

							<div id={`phase-${phase.id}`} className={(expandedPhases[phase.id] ?? (phase.state === 'ACTIVE')) ? 'mt-2' : 'mt-2 hidden'}>
								<div className='flex flex-row'>
									{phase.phaseGroups?.nodes.map((phaseGroup, phaseIndex) =>
										<div className='flex flex-col'>
											<a className="" href={phaseGroup.bracketUrl} target='_blank' rel="noreferrer">Pool #{phaseIndex + 1} - {phaseGroup.id}</a>
											<div key={phaseGroup.id} className={`ml-8 ${phase.bracketType} ${phase.bracketType}_${phase.numSeeds} flex`}>
												{
													Object.keys(groupBy(phaseGroup.sets.nodes, 'round'))
														.sort((a, b) => Number(a) - Number(b))
														.map(round => (
															<div key={round} className={`round-group round_${round}`}>
																<h3>Round {round}</h3>
																{groupBy(phaseGroup.sets.nodes, 'round')[round].map(set => (
																	<div key={set.id}>
																		<BracketSet id={set.id} identifier={set.identifier} round={set.round} totalGames={set.totalGames} slots={set.slots} />
																	</div>
																))}
															</div>
														))
												}
											</div>
										</div>

									)}
								</div>
							</div>
						</div>
					)}
					<hr></hr>
				</div>
			)
		}
	</div>
}

export default BracketDashboard;